import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ordersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ShoppingCart, 
  User, 
  Building, 
  FileText, 
  CheckCircle,
  Check,
  Loader2,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, name: 'Service', icon: ShoppingCart },
  { id: 2, name: 'Account', icon: User },
  { id: 3, name: 'Company', icon: Building },
  { id: 4, name: 'Review', icon: FileText },
  { id: 5, name: 'Confirm', icon: CheckCircle },
];

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, login, register } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Service data from location state
  const service = location.state?.service;
  const calculatorData = location.state?.calculatorData;
  
  // Form data
  const [authMode, setAuthMode] = useState('login');
  const [authData, setAuthData] = useState({ email: '', password: '', company_name: '' });
  const [companyDetails, setCompanyDetails] = useState({
    company_name: '',
    registration_number: '',
    address: '',
    city: '',
    country: 'Zambia', // Pre-filled with Zambia
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    if (!service) {
      navigate('/services');
      return;
    }
    
    // If authenticated, skip to step 3
    if (isAuthenticated) {
      setCurrentStep(3);
      setCompanyDetails(prev => ({
        ...prev,
        company_name: user?.company_name || '',
        contact_email: user?.email || '',
      }));
    }
  }, [service, isAuthenticated, user]);

  const formatCurrencyUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getServicePrice = () => {
    if (calculatorData) {
      return calculatorData.total;
    }
    return service?.price || 0;
  };

  const getServiceName = () => {
    if (calculatorData) {
      return `EPR Plastic Packaging Licensing (Zambia ${calculatorData.year})`;
    }
    return service?.name || '';
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (authMode === 'login') {
        await login(authData.email, authData.password);
      } else {
        await register({
          email: authData.email,
          password: authData.password,
          company_name: authData.company_name,
          role: 'producer_admin'
        });
      }
      setCompanyDetails(prev => ({
        ...prev,
        company_name: authData.company_name || user?.company_name || '',
        contact_email: authData.email || user?.email || '',
      }));
      setCurrentStep(3);
      toast.success('Signed in successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const validateCompanyDetails = () => {
    const required = ['company_name', 'registration_number', 'address', 'city', 'country', 'contact_name', 'contact_email', 'contact_phone'];
    for (const field of required) {
      if (!companyDetails[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    
    try {
      const orderData = {
        items: [{
          service_id: service.id,
          service_name: getServiceName(),
          quantity: 1,
          unit_price: getServicePrice(),
          total_price: getServicePrice(),
        }],
        company_details: companyDetails,
        payment_method: 'pending',
        calculator_data: calculatorData || null,
      };
      
      const response = await ordersAPI.create(orderData);
      setOrderId(response.data.id);
      
      // Navigate to confirmation
      navigate('/checkout/confirmation', { 
        state: { 
          order: response.data,
          service: service 
        } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 3 && !validateCompanyDetails()) return;
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, isAuthenticated ? 3 : 1));
  };

  if (!service) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-[#056839]">Request Service</h1>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-between items-center">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center ${index > 0 ? 'flex-1' : ''}`}>
                  {index > 0 && (
                    <div className={`h-0.5 w-8 sm:w-16 md:w-24 mx-2 ${
                      currentStep > index ? 'bg-[#056839]' : 'bg-gray-200'
                    }`} />
                  )}
                  <div 
                    className={`flex flex-col items-center ${
                      currentStep >= step.id ? 'text-[#056839]' : 'text-[#6B7280]'
                    }`}
                    data-testid={`checkout-step-${step.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep > step.id 
                        ? 'bg-[#056839] text-white' 
                        : currentStep === step.id 
                          ? 'bg-[#69A488] text-white' 
                          : 'bg-gray-200 text-[#6B7280]'
                    }`}>
                      {currentStep > step.id ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <step.icon className="h-5 w-5" />
                      )}
                    </div>
                    <span className="text-xs mt-1 hidden sm:block">{step.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              {/* Step 1: Service Review */}
              {currentStep === 1 && (
                <div className="animate-fade-in" data-testid="step-1-content">
                  <h2 className="text-xl font-semibold text-[#056839] mb-4">Selected Service</h2>
                  <div className="p-4 bg-[#D2E4D6]/30 rounded-lg mb-6">
                    <h3 className="font-semibold text-[#3F3F3F]">{getServiceName()}</h3>
                    <p className="text-[#6B7280] text-sm mt-1">{service.description}</p>
                    {calculatorData && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-[#6B7280]">Based on your calculator inputs:</p>
                        {calculatorData.items?.map((item, idx) => (
                          <p key={idx} className="text-sm text-[#3F3F3F]">
                            • {item.material_type}: {item.quantity_tonnes?.toLocaleString() || item.quantity_kg?.toLocaleString()} {item.quantity_tonnes ? 'tonnes' : 'kg'}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={nextStep} className="btn-primary" data-testid="step-1-next">
                    Continue to Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Step 2: Account */}
              {currentStep === 2 && !isAuthenticated && (
                <div className="animate-fade-in" data-testid="step-2-content">
                  <h2 className="text-xl font-semibold text-[#056839] mb-4">
                    {authMode === 'login' ? 'Sign In' : 'Create Account'}
                  </h2>
                  
                  <div className="flex gap-4 mb-6">
                    <Button
                      variant={authMode === 'login' ? 'default' : 'outline'}
                      onClick={() => setAuthMode('login')}
                      className={authMode === 'login' ? 'btn-primary' : ''}
                    >
                      Sign In
                    </Button>
                    <Button
                      variant={authMode === 'register' ? 'default' : 'outline'}
                      onClick={() => setAuthMode('register')}
                      className={authMode === 'register' ? 'btn-primary' : ''}
                    >
                      Create Account
                    </Button>
                  </div>

                  <form onSubmit={handleAuth} className="space-y-4">
                    {authMode === 'register' && (
                      <div>
                        <Label>Company Name</Label>
                        <Input
                          value={authData.company_name}
                          onChange={(e) => setAuthData({ ...authData, company_name: e.target.value })}
                          placeholder="Your Company Ltd"
                          className="mt-1"
                          data-testid="checkout-company-name"
                        />
                      </div>
                    )}
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={authData.email}
                        onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
                        placeholder="you@company.com"
                        className="mt-1"
                        data-testid="checkout-email"
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={authData.password}
                        onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                        placeholder="••••••••"
                        className="mt-1"
                        data-testid="checkout-password"
                      />
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <Button type="button" variant="outline" onClick={prevStep}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                      </Button>
                      <Button type="submit" className="btn-primary flex-1" disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                          <>
                            Continue
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 3: Company Details */}
              {currentStep === 3 && (
                <div className="animate-fade-in" data-testid="step-3-content">
                  <h2 className="text-xl font-semibold text-[#056839] mb-4">Company Details</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        value={companyDetails.company_name}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, company_name: e.target.value })}
                        className="mt-1"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label>Registration Number *</Label>
                      <Input
                        value={companyDetails.registration_number}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, registration_number: e.target.value })}
                        placeholder="e.g., ZM-2024-12345"
                        className="mt-1"
                        data-testid="registration-number-input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address *</Label>
                      <Input
                        value={companyDetails.address}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, address: e.target.value })}
                        className="mt-1"
                        data-testid="address-input"
                      />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input
                        value={companyDetails.city}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, city: e.target.value })}
                        className="mt-1"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label>Country *</Label>
                      <div className="mt-1 h-10 px-4 border rounded-md bg-[#D2E4D6]/30 flex items-center">
                        <span className="text-[#056839] font-medium">Zambia</span>
                      </div>
                      <p className="text-xs text-[#6B7280] mt-1">AfriCycle Zambia operates in Zambia only</p>
                    </div>
                    <div>
                      <Label>Contact Name *</Label>
                      <Input
                        value={companyDetails.contact_name}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, contact_name: e.target.value })}
                        className="mt-1"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label>Contact Email *</Label>
                      <Input
                        type="email"
                        value={companyDetails.contact_email}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, contact_email: e.target.value })}
                        className="mt-1"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Contact Phone *</Label>
                      <Input
                        value={companyDetails.contact_phone}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, contact_phone: e.target.value })}
                        placeholder="+260 97 123 4567"
                        className="mt-1"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-6">
                    {!isAuthenticated && (
                      <Button type="button" variant="outline" onClick={prevStep}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                      </Button>
                    )}
                    <Button onClick={nextStep} className="btn-primary flex-1" data-testid="step-3-next">
                      Continue to Review
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Review */}
              {currentStep === 4 && (
                <div className="animate-fade-in" data-testid="step-4-content">
                  <h2 className="text-xl font-semibold text-[#056839] mb-4">Order Review</h2>
                  
                  <div className="space-y-6">
                    <div className="p-4 bg-[#D2E4D6]/30 rounded-lg">
                      <h3 className="font-semibold text-[#056839] mb-2">Service</h3>
                      <p className="text-[#3F3F3F]">{getServiceName()}</p>
                      {!service.price_on_request && (
                        <p className="text-2xl font-bold text-[#056839] mt-2">{formatCurrencyUSD(getServicePrice())}</p>
                      )}
                      {service.price_on_request && (
                        <p className="text-lg font-semibold text-[#056839] mt-2">Price upon request</p>
                      )}
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h3 className="font-semibold text-[#056839] mb-2">Company Information</h3>
                      <div className="text-sm text-[#3F3F3F] space-y-1">
                        <p>{companyDetails.company_name}</p>
                        <p>{companyDetails.address}</p>
                        <p>{companyDetails.city}, {companyDetails.country}</p>
                        <p className="text-[#6B7280]">Reg: {companyDetails.registration_number}</p>
                      </div>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h3 className="font-semibold text-[#056839] mb-2">Contact</h3>
                      <div className="text-sm text-[#3F3F3F] space-y-1">
                        <p>{companyDetails.contact_name}</p>
                        <p>{companyDetails.contact_email}</p>
                        <p>{companyDetails.contact_phone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-6">
                    <Button type="button" variant="outline" onClick={prevStep}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                    <Button onClick={nextStep} className="btn-primary flex-1" data-testid="step-4-next">
                      Continue to Confirm
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 5: Confirmation (No Payment) */}
              {currentStep === 5 && (
                <div className="animate-fade-in" data-testid="step-5-content">
                  <h2 className="text-xl font-semibold text-[#056839] mb-4">Confirm Your Request</h2>
                  
                  {/* Notice about payment */}
                  <div className="p-6 bg-[#E4D5B7]/30 rounded-xl border border-[#E4D5B7] mb-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#A0522D] text-white flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#056839] mb-2">No Online Payment Required</h3>
                        <p className="text-[#3F3F3F] text-sm leading-relaxed">
                          After you submit your request, our team will contact you shortly to confirm your order 
                          and guide you through the payment process.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="p-4 bg-[#D2E4D6]/30 rounded-lg mb-6">
                    <h3 className="font-semibold text-[#056839] mb-3">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Service:</span>
                        <span className="text-[#3F3F3F] font-medium">{getServiceName()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Company:</span>
                        <span className="text-[#3F3F3F]">{companyDetails.company_name}</span>
                      </div>
                      {!service.price_on_request && (
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="text-[#056839] font-semibold">Estimated Total:</span>
                          <span className="text-xl font-bold text-[#056839]">{formatCurrencyUSD(getServicePrice())}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button type="button" variant="outline" onClick={prevStep}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                    <Button 
                      onClick={handlePlaceOrder} 
                      className="btn-primary flex-1" 
                      disabled={loading}
                      data-testid="place-order-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Request
                          <CheckCircle className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 sticky top-24">
              <h3 className="font-semibold text-[#056839] mb-4">Order Summary</h3>
              
              <div className="space-y-3 pb-4 border-b border-gray-200">
                <div className="flex justify-between">
                  <span className="text-[#3F3F3F] text-sm">{getServiceName()}</span>
                </div>
                {!service.price_on_request && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Subtotal</span>
                      <span className="text-[#3F3F3F]">{formatCurrencyUSD(getServicePrice())}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Tax</span>
                      <span className="text-[#3F3F3F]">{formatCurrencyUSD(0)}</span>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex justify-between pt-4">
                <span className="font-semibold text-[#056839]">Total</span>
                {service.price_on_request ? (
                  <span className="text-lg font-semibold text-[#056839]">TBD</span>
                ) : (
                  <span className="text-2xl font-bold text-[#056839]">{formatCurrencyUSD(getServicePrice())}</span>
                )}
              </div>

              {/* Payment Notice */}
              <div className="mt-4 p-3 bg-[#E4D5B7]/30 rounded-lg">
                <p className="text-xs text-[#A0522D]">
                  <strong>Note:</strong> Online payment is not available. Our team will contact you to arrange payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
